import { useLayoutEffect, useRef } from 'react';
import { formatClock } from '../../shared/format';
import type { ActiveJoker, AuthorSeat, PublicRound } from '../../shared/types';
import { useFollowScroll } from '../lib/followScroll';
import { tint } from '../lib/tint';

/**
 * The projection's story stage (2026-07-18 revision): text on the right,
 * margin annotations on the left — like comment notes in a document. Jokers
 * are anchored where they started (live countdown while active, an end
 * marker where they ran out), penalties and time credits where they were
 * called. Discrete but informative; they double as reading context after
 * the round (F-22: constraint windows marked for the anthology).
 *
 * Anchoring: invisible inline markers are injected into the text at each
 * annotation's character position; after every render the notes take the
 * marker's measured offsetTop, and overlapping notes push each other down.
 */

interface Note {
  key: string;
  textPos: number;
  kind: 'joker' | 'jokerEnd' | 'penalty' | 'bonus';
  title: string;
  meta: string | null;
}

export function AnnotatedStory(props: {
  round: PublicRound;
  authors: AuthorSeat[];
  now: number;
  clockOffset: number;
  showCaret: boolean;
  className?: string;
}) {
  const { round, authors, now, clockOffset, showCaret, className } = props;
  const stageRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const noteRefs = useRef(new Map<string, HTMLDivElement>());

  useFollowScroll(stageRef, round.text.length);

  const authorName = (i: number) => authors[i]?.name ?? `Autor:in ${i + 1}`;
  const targetLabel = (j: ActiveJoker) =>
    j.target === 'global' ? 'beide' : authorName(j.target);

  const notes: Note[] = [];
  for (const j of round.endedJokers) {
    notes.push({
      key: `j-${j.instanceId}`,
      textPos: j.startTextPos,
      kind: 'joker',
      title: j.name,
      meta: `${targetLabel(j)} · vorbei`,
    });
    if (j.endTextPos !== null && j.endTextPos > j.startTextPos) {
      notes.push({
        key: `je-${j.instanceId}`,
        textPos: j.endTextPos,
        kind: 'jokerEnd',
        title: `${j.name} endet`,
        meta: null,
      });
    }
  }
  for (const j of round.activeJokers) {
    const remaining =
      j.remainingMsAtPause !== null
        ? j.remainingMsAtPause
        : j.endsAt === null
          ? null
          : Math.max(0, j.endsAt - (now + clockOffset));
    notes.push({
      key: `j-${j.instanceId}`,
      textPos: j.startTextPos,
      kind: 'joker',
      title: j.name,
      meta: `${targetLabel(j)} · ${remaining === null ? 'läuft' : `noch ${formatClock(remaining)}`}`,
    });
  }
  for (const [i, m] of round.marks.entries()) {
    const seconds = Math.round(Math.abs(m.deltaMs) / 1000);
    notes.push({
      key: `m-${i}`,
      textPos: m.textPos,
      kind: m.deltaMs < 0 ? 'penalty' : 'bonus',
      title: `${m.deltaMs < 0 ? '−' : '+'}${seconds} s ${authorName(m.author)}`,
      meta: m.reason,
    });
  }
  notes.sort((a, b) => a.textPos - b.textPos || a.key.localeCompare(b.key));

  // Cut the text at segment boundaries and note anchors; markers carry the
  // note keys anchored at that position.
  const cuts = new Set<number>([0, round.text.length]);
  for (const s of round.segments) cuts.add(s.start);
  for (const n of notes) cuts.add(Math.min(n.textPos, round.text.length));
  const positions = [...cuts].sort((a, b) => a - b);
  const notesAt = (pos: number) => notes.filter((n) => Math.min(n.textPos, round.text.length) === pos);

  const segmentAt = (pos: number) => {
    let author: number | undefined;
    for (const s of round.segments) {
      if (s.start <= pos) author = s.author;
      else break;
    }
    return author;
  };

  const pieces: React.ReactNode[] = [];
  for (let i = 0; i < positions.length; i++) {
    const a = positions[i]!;
    for (const n of notesAt(a)) {
      pieces.push(<span key={`a-${n.key}`} className={`anchor anchor--${n.kind}`} data-anchor={n.key} />);
    }
    const b = positions[i + 1];
    if (b === undefined || b <= a) continue;
    const author = segmentAt(a);
    const seat = author === undefined ? undefined : authors[author];
    pieces.push(
      <span
        key={`t-${a}`}
        className="story__segment"
        style={seat ? { background: tint(seat.color, 0.18) } : undefined}
        data-author={author}
      >
        {round.text.slice(a, b)}
      </span>,
    );
  }

  // Position the margin notes at their anchors; overlapping notes stack.
  useLayoutEffect(() => {
    const textEl = textRef.current;
    if (!textEl) return;
    const anchors = new Map<string, HTMLElement>();
    for (const el of textEl.querySelectorAll<HTMLElement>('[data-anchor]')) {
      anchors.set(el.dataset['anchor']!, el);
    }
    let floor = 0;
    for (const n of notes) {
      const noteEl = noteRefs.current.get(n.key);
      const anchorEl = anchors.get(n.key);
      if (!noteEl || !anchorEl) continue;
      const top = Math.max(anchorEl.offsetTop, floor);
      noteEl.style.top = `${top}px`;
      noteEl.style.visibility = 'visible';
      floor = top + noteEl.offsetHeight + 8;
    }
  });

  return (
    <div ref={stageRef} className={`annostage ${className ?? ''}`} data-testid="anno-stage">
      <div className="annostage__margin" aria-label="Anmerkungen">
        {notes.map((n) => (
          <div
            key={n.key}
            ref={(el) => {
              if (el) noteRefs.current.set(n.key, el);
              else noteRefs.current.delete(n.key);
            }}
            className={`note note--${n.kind}`}
            data-testid={n.kind === 'joker' ? 'annotation-joker' : n.kind === 'jokerEnd' ? 'annotation-joker-end' : 'annotation-mark'}
          >
            <span className="note__title">{n.title}</span>
            {n.meta && <span className="note__meta">{n.meta}</span>}
          </div>
        ))}
      </div>
      <div ref={textRef} className="annostage__text story story--beam" data-testid="story-text">
        {round.text.length === 0 && round.phase !== 'ended' && <span className="story__empty">…</span>}
        {pieces}
        {showCaret && round.phase === 'running' && (
          <span
            className="story__caret"
            style={{ background: authors[round.activeAuthor]?.color ?? '#fff7ec' }}
            aria-hidden="true"
          />
        )}
      </div>
    </div>
  );
}
